import { useCallback, useEffect, useRef, useState } from 'react';
import { useEvent } from 'react-use';
import { useField } from 'formik';
import { get } from 'lodash-es';
import { SetRequired } from 'type-fest';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import {
    TValidationTestParams,
    useFormContext
} from '../HBFormFields/HBFormProvider';

type PropsToBeRenamed = {
    onBlur?: Function;
    onChange?: Function;
};

type OmittedProps<InputProps extends PropsToBeRenamed> = Omit<
    InputProps,
    keyof PropsToBeRenamed | 'value' | 'error' | 'errorMessage'
>;

type RenamedProps<InputProps extends PropsToBeRenamed> = {
    /** Called after Formik commits the change. Use instead of `onChange` — Formik owns that prop directly. Pass a callback to run side effects (analytics, dependent field updates, etc.) after the field value updates in the form state. */
    onChangeCallback?: InputProps['onChange'];
    /** Called after Formik processes blur. Use instead of `onBlur` — Formik owns that prop directly. */
    onBlurCallback?: InputProps['onBlur'];
};

/** Formik-aware wrapper type: removes value/error/errorMessage/onChange/onBlur from the underlying field props, adds callback-renamed versions, and requires `id` as the Formik field key. */
export type FormFieldProps<FieldProps extends PropsToBeRenamed> =
    OmittedProps<FieldProps> &
        RenamedProps<FieldProps> &
        SetRequired<MustIncludeOneOfBasicTestIdWithIdProps, 'id'>;

const useFormFieldProps = <FieldProps extends PropsToBeRenamed>(
    { onBlurCallback, onChangeCallback, ...props }: FormFieldProps<FieldProps>,
    shouldRespectUpdateOnBlur = false
) => {
    const [
        { onBlur, onChange, value: globalValue, ...fieldProps },
        { error, initialValue },
        { setValue }
    ] = useField(props.id);
    const { updateOnBlur: _updateOnBlur } = useFormContext();
    const updateOnBlur = shouldRespectUpdateOnBlur && _updateOnBlur;

    const [localValue, setLocalValue] = useState(initialValue);
    const localValueRef = useRef(localValue);
    localValueRef.current = localValue;
    const value = updateOnBlur ? localValue : globalValue;

    useEffect(() => {
        if (updateOnBlur) {
            setLocalValue(globalValue);
        }
    }, [updateOnBlur, globalValue]);

    useEvent('keydown', e => {
        if (updateOnBlur && e.key === 'Enter') {
            setValue(localValueRef.current);
        }
    });

    const _onBlur = useCallback(
        (...args) => {
            if (updateOnBlur) {
                setValue(localValue);
            }
            // @ts-ignore
            onBlur(...args);
            onBlurCallback?.(...args);
        },
        [updateOnBlur, localValue, onBlur, onBlurCallback, setValue]
    );

    const _onChange = useCallback(
        (...args) => {
            const eventOrValue = args[0];
            if (eventOrValue?.nativeEvent) {
                const newValue = eventOrValue.target.value;
                if (updateOnBlur) {
                    setLocalValue(newValue);
                } else {
                    // @ts-ignore
                    onChange(...args);
                }
            } else {
                if (updateOnBlur) {
                    setLocalValue(eventOrValue);
                } else {
                    setValue(eventOrValue);
                }
            }
            onChangeCallback?.(...args);
        },
        [updateOnBlur, onChange, onChangeCallback, setValue]
    );

    return {
        error: !!error,
        errorMessage: error,
        initialValue,
        onBlur: _onBlur,
        onChange: _onChange,
        value,
        ...fieldProps,
        ...props
    };
};

export default useFormFieldProps;

export const useFormInputProps = <InputProps extends PropsToBeRenamed>(
    props: FormFieldProps<InputProps>,
    shouldRespectUpdateOnBlur = false
) => {
    const { errorMessage, ...inputProps } = useFormFieldProps(
        props,
        shouldRespectUpdateOnBlur
    );
    return inputProps;
};

/** HTML5 validation attribute names automatically extracted from the Yup schema. */
export type TFormInputValidationProps =
    | 'required'
    | 'maxLength'
    | 'max'
    | 'min';

export const useFormInputValidationProps = (id: string) => {
    const { validationSchema } = useFormContext();
    const parsed = getFieldValidationTests(validationSchema, id);
    if (!parsed) {
        return;
    }
    const { required, integer, positive, min, max, type } = parsed;

    return {
        required,
        ...(type === 'number'
            ? {
                  ...(integer && { step: 1 }),
                  ...(positive && { min: 0 }),
                  ...min,
                  ...max
              }
            : {
                  ...(max && { maxLength: max.max })
              })
    };
};

/** Parsed HTML5-validation-relevant facts pulled off a field's Yup tests — shared by useFormInputValidationProps and useHBFormField. */
export interface TFieldValidationTests {
    required?: boolean;
    integer?: boolean;
    positive?: boolean;
    min?: TValidationTestParams;
    max?: TValidationTestParams;
    type?: string;
}

/**
 * Resolves a field's Yup schema node from a described `validationSchema`, and parses its
 * `required`/`integer`/`positive`/`min`/`max` tests into a flat shape. Handles dot-nested ids
 * (`address.city` → `address.fields.city`) since described schemas nest sub-fields under `.fields.`.
 */
export function getFieldValidationTests(
    validationSchema: ReturnType<typeof useFormContext>['validationSchema'],
    id: string
): TFieldValidationTests | undefined {
    if (!validationSchema) {
        return undefined;
    }

    const key: keyof typeof validationSchema = 'fields';
    const field = get(validationSchema[key], id.split('.').join(`.${key}.`));
    if (!field) {
        return undefined;
    }

    const { tests, type } = field;
    let required: boolean | undefined =
            'optional' in field ? !field.optional : undefined,
        integer: boolean,
        positive: boolean,
        min: TValidationTestParams,
        max: TValidationTestParams;
    tests?.forEach(({ name, params }) => {
        switch (name) {
            case 'required':
                required = required ?? true;
                break;
            case 'integer':
                integer = true;
                break;
            case 'positive':
                positive = true;
                break;
            case 'min':
                min = params;
                break;
            case 'max':
                max = params;
                break;
        }
    });

    return { required, integer, positive, min, max, type };
}
